
import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Building2 } from 'lucide-react';
import { format } from 'date-fns';
import { Input } from '@/components/ui/input';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import { API } from '../../utils/api';
import { AdminLogin } from './AdminLogin';
import { AdminVendors } from './AdminVendors';
import { AdminUsers } from './AdminUsers';
import { AdminPlans } from './AdminPlans';
import { AdminSettings } from './AdminSettings';
import { AdminWatches } from './AdminWatches';
import { AdminSystem } from './AdminSystem';
import { AdminModules } from './AdminModules';
import { AdminTransactions } from './AdminTransactions';
import { AdminServiceTest } from './AdminServiceTest';
import { AdminBusiness } from './AdminBusiness';
import { AdminDevConfig } from './AdminDevConfig';

export function AdminDashboard() {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [admin, setAdmin] = useState(() => {
    const u = localStorage.getItem('admin_user');
    return u ? JSON.parse(u) : null;
  });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [metrics, setMetrics] = useState(null);
  const [systemSettings, setSystemSettings] = useState(null);
  const [period, setPeriod] = useState('month');
  const [month, setMonth] = useState('');
  const [pendingBizCount, setPendingBizCount] = useState(0);
  
  const headers = useMemo(() => ({ Authorization: token ? `Bearer ${token}` : '' }), [token]);

  useEffect(() => {
    if (!token) return;
    // Fetch pending business count for sidebar badge
    axios.get(`${API}/admin/pending-businesses`, { headers })
      .then(res => setPendingBizCount(res.data.count || 0))
      .catch(() => {});
  }, [token, headers, activeTab]);

  useEffect(() => {
    if (activeTab !== 'dashboard' || !token) return;
    const fetchMetrics = async () => {
      try {
        const params = { period };
        if (period === 'select' && month) params.month = month;
        const res = await axios.get(`${API}/admin/metrics`, { headers, params });
        setMetrics(res.data);
        const settingsRes = await axios.get(`${API}/admin/settings`, { headers });
        setSystemSettings(settingsRes.data);
      } catch (e) {
        console.error('Failed to load metrics', e);
      }
    };
    fetchMetrics();
  }, [activeTab, period, month, token, headers]);
  
  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setToken(null);
    setAdmin(null);
  };
  
  const changePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, { headers });
      
      toast.success('Admin password changed (session)');
      setShowPasswordChange(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    }
  };

  if (!token) return <AdminLogin onLogin={(t, a) => { setToken(t); setAdmin(a); }} />;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r min-h-screen p-4 flex flex-col">
        <div className="mb-8">
          <h1 className="text-xl font-bold text-gray-800">Admin Panel V7.21</h1>
          <div className="text-xs text-gray-500 mt-1">Logged in as {admin?.username}</div>
        </div>
        
        <nav className="space-y-2 flex-1">
          <Button variant={activeTab === 'dashboard' ? 'default' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('dashboard')}>
            📊 Dashboard
          </Button>
          <Button variant={activeTab === 'vendors' ? 'default' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('vendors')}>
            🏪 Vendors
          </Button>
          <Button variant={activeTab === 'parents' ? 'default' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('parents')}>
            👨‍👩‍👧 Payers
          </Button>
          <Button variant={activeTab === 'mobpay' ? 'default' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('mobpay')}>
            📱 Buddys
          </Button>
          <Button variant={activeTab === 'business' ? 'default' : 'ghost'} className="w-full justify-start gap-2" onClick={() => setActiveTab('business')}>
            <Building2 className="h-4 w-4" /> Business
            {pendingBizCount > 0 && <Badge variant="destructive" className="ml-auto h-5 min-w-5 text-[10px] px-1.5">{pendingBizCount}</Badge>}
          </Button>
          <Button variant={activeTab === 'transactions' ? 'default' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('transactions')}>
            🧾 Transactions
          </Button>
          <Button variant={activeTab === 'plans' ? 'default' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('plans')}>
            💳 Plans
          </Button>
          <Button variant={activeTab === 'modules' ? 'default' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('modules')}>
            📦 Modules
          </Button>
          <Button variant={activeTab === 'watches' ? 'default' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('watches')}>
            ⌚ Watch Stock
          </Button>
          <Button variant={activeTab === 'settings' ? 'default' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('settings')}>
            ⚙️ Settings
          </Button>
          <Button variant={activeTab === 'service-test' ? 'default' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('service-test')}>
            🔧 Service Test
          </Button>
          <Button variant={activeTab === 'dev-config' ? 'default' : 'ghost'} className="w-full justify-start" onClick={() => setActiveTab('dev-config')} data-testid="admin-tab-dev-config">
            🛠️ Dev Config
          </Button>
          <Button variant={activeTab === 'system' ? 'default' : 'ghost'} className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setActiveTab('system')}>
            ⚠️ System
          </Button>
        </nav>

        <div className="pt-4 border-t space-y-2">
          <Button variant="outline" className="w-full" onClick={() => setShowPasswordChange(!showPasswordChange)}>
            Change Password
          </Button>
          <Button variant="destructive" className="w-full" onClick={logout}>
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8 overflow-auto">
        {showPasswordChange && (
          <Card className="mb-6">
            <CardHeader><CardTitle>Change Admin Password</CardTitle></CardHeader>
            <CardContent className="space-y-3 max-w-md">
              <Input type="password" placeholder="Current Password" value={passwordForm.current_password} onChange={e=>setPasswordForm({...passwordForm, current_password: e.target.value})} />
              <Input type="password" placeholder="New Password" value={passwordForm.new_password} onChange={e=>setPasswordForm({...passwordForm, new_password: e.target.value})} />
              <Input type="password" placeholder="Confirm New Password" value={passwordForm.confirm_password} onChange={e=>setPasswordForm({...passwordForm, confirm_password: e.target.value})} />
              <div className="flex gap-2">
                <Button onClick={changePassword}>Update</Button>
                <Button variant="secondary" onClick={()=>setShowPasswordChange(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Performance Metrics</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-4 items-end">
                <div className="w-48">
                  <label className="text-xs text-muted-foreground">Period</label>
                  <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger data-testid="metrics-period-select">
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="month">Current Month</SelectItem>
                      <SelectItem value="ytd">Year to Date</SelectItem>
                      <SelectItem value="select">Select Month</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {period === 'select' && (
                  <div>
                    <label className="text-xs text-muted-foreground">Month</label>
                    <Input
                      type="month"
                      value={month}
                      onChange={(e) => setMonth(e.target.value)}
                      data-testid="metrics-month-input"
                    />
                  </div>
                )}
                {metrics && (
                  <div className="text-xs text-muted-foreground">
                    Range: {format(new Date(metrics.start), 'yyyy-MM-dd')} → {format(new Date(metrics.end), 'yyyy-MM-dd')}
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader><CardTitle>Users</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div>Payers signed up: <strong>{metrics?.users?.payers_signed_up ?? 0}</strong></div>
                  <div>Buddys signed up: <strong>{metrics?.users?.buddies_signed_up ?? 0}</strong></div>
                  <div>Business accounts: <strong>{metrics?.users?.business_users ?? 0}</strong></div>
                  <div>Active Payers (7d): <strong>{metrics?.users?.active_payers_7d ?? 0}</strong></div>
                  <div>Active Buddys (7d): <strong>{metrics?.users?.active_buddies_7d ?? 0}</strong></div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Transactions</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div>New payments: <strong>{metrics?.transactions?.payers_new_payments ?? 0}</strong></div>
                  <div>New requests sent: <strong>{metrics?.transactions?.payers_new_requests_sent ?? 0}</strong></div>
                  <div>Requests received: <strong>{metrics?.transactions?.payers_received_requests ?? 0}</strong></div>
                  <div>Total transactions: <strong>{metrics?.transactions?.total_transactions ?? 0}</strong></div>
                  <div>Total value: <strong>{metrics?.transactions?.total_value ?? 0} SEK</strong></div>
                  <div>Users with activity: <strong>{metrics?.transactions?.payers_with_transactions ?? 0}</strong></div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Business & Subscriptions</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="font-medium text-xs text-muted-foreground mb-1">Business</div>
                  <div>Active sub-accounts: <strong>{metrics?.business?.active_sub_accounts ?? 0}</strong></div>
                  <div>New invitations: <strong>{metrics?.business?.new_invitations ?? 0}</strong></div>
                  <div className="font-medium text-xs text-muted-foreground mt-3 mb-1">Subscriptions</div>
                  {(metrics?.subscriptions || []).length === 0 && <div>No active subscriptions</div>}
                  {(metrics?.subscriptions || []).map((sub) => (
                    <div key={sub.plan_id}>{sub.plan_name}: <strong>{sub.count}</strong></div>
                  ))}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Default Limits</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="font-medium">Global Limits</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="global-limits">
                  <div>Per Day: <strong>{systemSettings?.global_max_per_day ?? 0} SEK</strong></div>
                  <div>Per Month: <strong>{systemSettings?.global_max_per_month ?? 0} SEK</strong></div>
                  <div>Per Transaction: <strong>{systemSettings?.global_max_per_transaction ?? 0} SEK</strong></div>
                </div>
                <div className="font-medium">Buddy Type Limits</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="buddy-type-limits">
                  {Object.entries(systemSettings?.buddy_type_limits || {}).map(([key, limits]) => (
                    <div key={key} className="border rounded-md p-2">
                      <div className="font-semibold capitalize">{key}</div>
                      <div className="text-xs">Daily: {limits.daily} SEK</div>
                      <div className="text-xs">Monthly: {limits.monthly} SEK</div>
                      <div className="text-xs">Per Tx: {limits.per_tx} SEK</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {activeTab === 'vendors' && <AdminVendors headers={headers} />}
        {activeTab === 'parents' && <AdminUsers headers={headers} role="parent" />}
        {activeTab === 'mobpay' && <AdminUsers headers={headers} role="mobpay" />}
        {activeTab === 'business' && <AdminBusiness headers={headers} />}
        {activeTab === 'transactions' && <AdminTransactions headers={headers} />}
        {activeTab === 'plans' && <AdminPlans headers={headers} />}
        {activeTab === 'modules' && <AdminModules headers={headers} />}
        {activeTab === 'watches' && <AdminWatches headers={headers} />}
        {activeTab === 'settings' && <AdminSettings headers={headers} />}
        {activeTab === 'service-test' && <AdminServiceTest headers={headers} />}
        {activeTab === 'dev-config' && <AdminDevConfig headers={headers} />}
        {activeTab === 'system' && <AdminSystem headers={headers} />}
      </div>
      <Toaster richColors />
    </div>
  );
}
